import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { BRAND_DARK, BRAND_LIGHT } from "@/shared/constants.js";
import { formatLocationOneLine, formatSubmissionListDate } from "@/shared/utils/format.js";
import { primaryLocationFromList } from "@/modules/Quote/utils/submission.js";
import { fetchInsuredContacts, fetchInsuredDetails, fetchInsuredLocations } from "@/modules/Profile/api/profileApi.js";
import { ContactResponse, InsuredDataResponse, LocationMasterResponse } from "@/shared/dtos/index.js";
import { ArrowLeft } from "@/shared/components/Icon.jsx";
import { btnOutline } from "@/shared/utils/styles.js";
import { useStore } from "@/shared/store/useStore.js";
import insuredProfileStore from "@/shared/store/insuredProfileStore.js";
import ilfDlfStore from "@/modules/Quote/store/ilfDlfStore.js";
import specialityStore from "@/modules/Quote/store/specialityStore.js";
import sessionStore from "@/shared/store/sessionStore.js";
import modalStore from "@/shared/store/modalStore.js";
import { MedMalGuardHeader } from "@/modules/Quote/components/MedMalGuardLanding.jsx";

export default function ProfilePage() {
  const navigate = useNavigate();
  const insuredProfile  = useStore(insuredProfileStore, (s) => s.insuredProfile);
  const isAuthenticated = Boolean(insuredProfile?.id || insuredProfile?.name || insuredProfile?.username);
  const sessionReady    = useStore(sessionStore, (s) => s.sessionReady);

  // Page-owned data. Profile details and locations are only displayed
  // on this page, so they live and die with it — no need to keep them
  // in the global context.
  const [insuredDetails, setInsuredDetails] = useState(null);
  const [insuredDetailsLoading, setInsuredDetailsLoading] = useState(false);
  const [insuredLocations, setInsuredLocations] = useState([]);
  const [insuredLocationsLoading, setInsuredLocationsLoading] = useState(false);
  const [insuredContacts, setInsuredContacts] = useState([]);
  const [insuredContactsLoading, setInsuredContactsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setInsuredDetailsLoading(true);
    fetchInsuredDetails()
      .then(/** @param {InsuredDataResponse} data */ (data) => { if (!cancelled) setInsuredDetails(data); })
      .catch(() => { if (!cancelled) setInsuredDetails(null); })
      .finally(() => { if (!cancelled) setInsuredDetailsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setInsuredLocationsLoading(true);
    fetchInsuredLocations()
      .then(/** @param {LocationMasterResponse[]} list */ (list) => { if (!cancelled) setInsuredLocations(list); })
      .catch(() => { if (!cancelled) setInsuredLocations([]); })
      .finally(() => { if (!cancelled) setInsuredLocationsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setInsuredContactsLoading(true);
    fetchInsuredContacts()
      .then(/** @param {ContactResponse[]} list */ (list) => { if (!cancelled) setInsuredContacts(list); })
      .catch(() => { if (!cancelled) setInsuredContacts([]); })
      .finally(() => { if (!cancelled) setInsuredContactsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const primaryContact = insuredContacts.find((c) => c?.isprimary) || insuredContacts[0] || null;

  if (sessionReady && !isAuthenticated) return <Navigate to="/signin" replace />;

  return (
    // Mobile shows the shared MedMalGuard header (logo + hamburger) plus a
    // slim back-arrow bar. Desktop (>=1024px, via .profile-page CSS in
    // responsive.css) escapes the card and lays the info cards out in a
    // centered two-column grid.
    <div className="profile-page">
      {/* MedMalGuard header at every viewport — collapses to logo +
          hamburger on phones, same as the landing page. */}
      <div className="app-header">
        <MedMalGuardHeader />
      </div>

      {/* Mobile-only back bar — hidden on desktop; the header above
          carries the Dashboard link instead. */}
      <div className="profile-mobile-topbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px 8px", borderBottom: "1px solid #f0f0f0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}
          >
            <ArrowLeft />
          </button>
        </div>
      </div>

      <div className="profile-body" style={{ flex: 1, padding: "0 18px 18px", overflowY: "auto", display: "flex", flexDirection: "column" }}>
       <div className="profile-card">
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 19, fontWeight: 600, color: "#1a1a1a", margin: "12px 0 4px" }}>My profile</h2>
        <p style={{ fontSize: 12, color: "#888", marginBottom: 14 }}>Your information as it appears on your application.</p>

        <div className="profile-grid">

        <div style={{ background: "#f7f7f5", borderRadius: 12, padding: "12px 14px", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "#333", marginBottom: 8 }}>Personal information</div>
          {[
            ["Name", (insuredContactsLoading && insuredDetailsLoading) ? "-" : (
              insuredDetails?.companyname
              || [primaryContact?.firstname, primaryContact?.lastname].filter(Boolean).join(" ")
              || [insuredDetails?.firstname, insuredDetails?.lastname].filter(Boolean).join(" ")
              || "-"
            )],
            ["Email", insuredContactsLoading ? "-" : (primaryContact?.email || insuredDetails?.email || insuredProfile?.username || insuredProfile?.name || "-")],
            ["Phone", insuredContactsLoading ? "-" : (primaryContact?.contactnumber || "-")],
            ["Date of birth", insuredDetailsLoading ? "-" : (insuredDetails?.dob ? formatSubmissionListDate(insuredDetails.dob) : "-")],
            ["PA license", insuredDetailsLoading ? "-" : (insuredDetails?.licenseNumber || insuredDetails?.licenseNum || insuredDetails?.licensenumber || insuredDetails?.license || "-")],
            ["NPI", insuredDetailsLoading ? "-" : (insuredDetails?.npiNumber || insuredDetails?.npi || "-")],
          ].map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 12, color: "#888" }}>{l}</span>
              <span style={{ fontSize: 12, fontWeight: 500, color: "#444" }}>{v}</span>
            </div>
          ))}
        </div>

        <div style={{ background: "#f7f7f5", borderRadius: 12, padding: "12px 14px", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "#333", marginBottom: 8 }}>Practice details</div>
          {[
            ["Classification", "PA 1 — Non-obstetrical"],
            ["Employment", "W-2 Employee"],
            ["Employer", "ABC Medical Group"],
            ["Address", insuredLocationsLoading ? "-" : formatLocationOneLine(primaryLocationFromList(insuredLocations))],
          ].map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 12, color: "#888" }}>{l}</span>
              <span style={{ fontSize: 12, fontWeight: 500, color: "#444", textAlign: "right", maxWidth: "55%" }}>{v}</span>
            </div>
          ))}
        </div>

        <div style={{ background: "#f7f7f5", borderRadius: 12, padding: "12px 14px", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "#333", marginBottom: 8 }}>Scope & setting</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
            {["Diagnostic Treatment", "Physical Exams", "General Medications", "Treatment Plans", "Routine Lab"].map((s) => (
              <span key={s} style={{ fontSize: 11, background: BRAND_LIGHT, color: BRAND_DARK, padding: "3px 8px", borderRadius: 5, fontWeight: 500 }}>{s}</span>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#888", marginTop: 6 }}>
            <strong style={{ color: "#666" }}>Setting:</strong> Primary Care Office
          </div>
        </div>

        <div style={{ background: "#f7f7f5", borderRadius: 12, padding: "12px 14px", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "#333", marginBottom: 8 }}>Compliance</div>
          {[
            ["Collaborative agreement", "Yes"],
            ["HIPAA compliant", "Yes"],
            ["Risk management course", "Yes"],
            ["Association member", "AAPA"],
          ].map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 12, color: "#888" }}>{l}</span>
              <span style={{ fontSize: 12, fontWeight: 500, color: "#444" }}>{v}</span>
            </div>
          ))}
        </div>

        </div>

        <button style={{ ...btnOutline, fontSize: 12, marginTop: 0 }}>Edit profile</button>
       </div>
      </div>

      <div className="profile-footer" style={{ padding: "6px 16px", borderTop: "1px solid #f0f0f0", textAlign: "center", fontSize: 10, color: "#ccc" }}>
        SelectFirst Insurance Services · (888) 966-3881 · <span onClick={() => { modalStore.showAbout = true; }} style={{ color: "#aaa", textDecoration: "underline", cursor: "pointer" }}>About</span> · <span onClick={() => { modalStore.showPrivacy = true; }} style={{ color: "#aaa", textDecoration: "underline", cursor: "pointer" }}>Privacy</span> · <span onClick={() => { modalStore.showTerms = true; }} style={{ color: "#aaa", textDecoration: "underline", cursor: "pointer" }}>Terms</span>
      </div>
    </div>
  );
}
